import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useRouter } from 'next/router';
import { FlyToInterpolator } from 'react-map-gl';
import cn from 'classnames';
import { get, keys } from 'lib/utils/localStorage';
import { reformatLocaleToContentfulLocale } from 'lib/utils/helpers';
import useIsMobile from 'hooks/utils/useIsMobile';
import { db } from 'lib/firebase';
import useVisitorTypeTracking from 'hooks/tracking/useVisitorTypeTracking';
import {
  handleSalonsInTwentyKmRadius,
  handleSalonsInFiftyKmRadius,
  handleSalonsInOneHundredKmRadius,
} from 'lib/utils/salonsHelpers';
import Layout from 'components/layout';
import Map from 'components/pages/salons/map';
import MatchesList from 'components/pages/salons/matchesList';
import SalonDetails from 'components/pages/salons/salonDetails';
import SearchBar from 'components/pages/salons/searchBar';
import Filter from 'components/pages/salons/searchFilter';
import Icon from 'components/shared/icon';
import styles from 'components/pages/salons/salons.module.scss';
import { getGlobal } from 'lib/contentful';
import { useSettings } from 'contexts/SettingContext';
import Head from 'next/head';
import { getSelectedSalonNo, getStorageCustomerSalonName } from 'lib/shopify';

export default function FindASalon({ salonData, globalModules, locale }) {
  const extractedCountries = Object.values(globalModules?.firstcountryselectorlocales);
  const allLocales = extractedCountries
    .map(country => country.countryList)
    .flat()
    .filter(Boolean);

  const resultsLimit = parseInt(globalModules.thirdsalonsfinderpage?.resultsLimit?.text, 10);

  const defaultLanguage = allLocales.find(
    singleLocale => singleLocale?.slug === `/${locale}/`
  )?.defaultLanguage;

  const rewriteLocales = allLocales.map(rLocale => {
    if (rLocale.slug) {
      return {
        ...rLocale,
        slug: rLocale?.slug.slice(1, rLocale.slug.length - 1),
      };
    }

    return rLocale;
  });

  const relatedCountries = rewriteLocales.find(
    singleLocale => singleLocale?.slug === locale
  )?.relatedCountries;

  const returnQueryString = countryArray => {
    let queryString = '';
    for (let i = 0; i < countryArray?.length; i++) {
      queryString += `|country:${countryArray[i].toLowerCase()}`;
    }
    return queryString;
  };
  const localeQuery = returnQueryString(relatedCountries);

  const {
    backButtonContent,
    getDirectionsPointer,
    keuneAmbassadorSalonContent,
    pageTitle,
    searchBarPlaceholder,
    searchFiltersPointer,
    searchFilterDescription,
    shopNowButton,
    shopNowContent,
    javascriptDisabledMessage,
  } = globalModules?.salonsfinderpage;
  const { secondsalonsfinderpage, thirdsalonsfinderpage } = globalModules;
  const filtersList = globalModules?.salonsfinderpage?.searchFilters?.texts;

  const defaultFilters = thirdsalonsfinderpage.defaultSelectedFilters?.texts;

  const formattedDefaultFilters = useMemo(() => {
    if (!defaultFilters || !defaultFilters.length) return [filtersList[0].toUpperCase()];

    const existingFilters = filtersList.map(f => f.toUpperCase());

    const result = defaultFilters
      .map(f => f.toUpperCase())
      .filter(f => existingFilters.includes(f));

    if (!result.length) return [filtersList[0].toUpperCase()];
    return result;
  }, [defaultFilters, filtersList]);

  const isAllSalonsFilterDefault = formattedDefaultFilters.includes(filtersList[0]?.toUpperCase());

  const matches = useRef(null);

  const { salons, setSalons } = useSettings();
  const [userCountryCode, setUserCountryCode] = useState('');
  const [disableSearch, setDisableSearch] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [active, setActive] = useState(filtersList[0].toUpperCase());
  const [activeFilters, setActiveFilters] = useState(formattedDefaultFilters);
  const [salonsInRadius, setSalonsInRadius] = useState(salons);
  const [selectedSalon, setSelectedSalon] = useState({});
  const [salonFocus, setSalonFocus] = useState({});
  const [showSalon, setShowSalon] = useState(false);
  const [offset, setOffset] = useState(0);
  const [headerOffset, setHeaderOffset] = useState(0);
  const [viewportTrigger, setViewportTrigger] = useState(true);
  const [initialViewport, setInitialViewport] = useState({ longitude: 0, latitude: 0 });
  const [viewport, setViewport] = useState({
    width: '100%',
    height: '100%',
    longitude: 0,
    latitude: 0,
    zoom: 6.5,
  });
  const [gaveGeoPermission, setGaveGeoPermission] = useState({ state: 'prompt' });
  const [hoverPin, setHoverPin] = useState(null);
  const [hovered, setHovered] = useState(null);
  const isMobile = useIsMobile();
  const router = useRouter();
  const customerEmail = get(keys.LOGGED_IN_CUSTOMER);

  const scrollUp = () => {
    window.scrollTo({ top: `${offset + headerOffset + 40}`, left: 0, behavior: 'smooth' });
  };

  const setGeolocation = (lat, lon) => {
    if (!gaveGeoPermission) setGaveGeoPermission(true);
    setViewport({ ...viewport, latitude: lat, longitude: lon, zoom: 14 });
    setInitialViewport({ ...viewport, latitude: lat, longitude: lon, zoom: 14 });
    setViewportTrigger(true);
    setSearchText(thirdsalonsfinderpage.myLocation.text);
    setSalons(salonData);
    if (loading) setLoading(false);
  };

  const fetchLocationThroughIP = () => {
    fetch('/api/geolocation')
      .then(res => res.json())
      .then(location => {
        const renderPage = () => {
          setUserCountryCode(location.countryCode);
          setViewport({
            ...viewport,
            latitude: location.lat,
            longitude: location.lon,
          });
          setInitialViewport({ latitude: location.lat, longitude: location.lon });
          setSalons(salonData);
          setLoading(false);
        };
        renderPage();
      })
      .catch(err => console.error(err));
  };

  const getGeolocation = () => {
    // Try to get location by the Browser API
    navigator.geolocation.getCurrentPosition(
      position => setGeolocation(position.coords.latitude, position.coords.longitude),
      () => {
        // If the browser API geolocation fails, use the IP geolocation
        fetchLocationThroughIP();
      }
    );
  };

  const decideLocationButtonText = () => {
    if (gaveGeoPermission.state === 'denied') return thirdsalonsfinderpage.noLocation.text;
    return thirdsalonsfinderpage.useLocation.text;
  };

  const handleZoomIn = (lat, lon) => {
    setViewport({
      ...viewport,
      longitude: lon,
      latitude: lat,
      zoom: 16,
      transitionDuration: 1000,
      transitionInterpolator: new FlyToInterpolator(),
    });
  };

  const handleSalonFocus = (salon, index) => {
    setShowSalon(true);
    setSalonFocus(salon);
    setHovered(index);
    handleZoomIn(Number(salon?.position?.latitude), Number(salon?.position?.longitude));

    if (window && window.innerWidth < 900) {
      scrollUp();
    }
  };

  const handleSalonsInRadius = salons => {
    const salonsInTwentyKmRadius = salons.filter(salon =>
      handleSalonsInTwentyKmRadius(
        {
          latitude: salon?.position?.latitude,
          longitude: salon?.position?.longitude,
        },
        viewport
      )
    );

    const salonsInFiftyKmRadius = salons.filter(salon =>
      handleSalonsInFiftyKmRadius(
        {
          latitude: salon?.position?.latitude,
          longitude: salon?.position?.longitude,
        },
        viewport
      )
    );

    const salonsInOneHundredKmRadius = salons.filter(salon =>
      handleSalonsInOneHundredKmRadius(
        {
          latitude: salon?.position?.latitude,
          longitude: salon?.position?.longitude,
        },
        viewport
      )
    );

    if (salonsInTwentyKmRadius.length > 0) {
      setViewportTrigger(false);
      setViewport({
        ...viewport,
      });
      return salonsInTwentyKmRadius;
    }
    if (salonsInFiftyKmRadius.length > 0) {
      setViewportTrigger(false);
      setViewport({
        ...viewport,
      });
      return salonsInFiftyKmRadius;
    }
    if (salonsInOneHundredKmRadius.length > 0) {
      setViewportTrigger(false);
      setViewport({
        ...viewport,
      });
      return salonsInOneHundredKmRadius;
    }
    return [];
  };

  const handleSearchClick = async input => {
    if (typeof input !== 'object') {
      const cleanedInput = input.replace('&', 'and').replace(/[^a-zA-Z0-9 ]/g, '') || '';
      const filtered = salons.filter(salon => {
        const { salonName, AddressLine2, zipcode, city, country } = salon;
        const values = [salonName, AddressLine2, zipcode, city, country];
        const loweredSearchText = searchText.toLowerCase();
        return values.find(
          value => value !== undefined && value.toLowerCase().includes(loweredSearchText)
        );
      });

      const getQuerry = () => {
        if (filtered.length !== 1) return cleanedInput;
        const q = [
          filtered[0].AddressLine2 || '',
          filtered[0].country || '',
          filtered[0].zipcode || '',
          filtered[0].city || '',
          filtered[0].salonName || '',
        ].join(',');
        return q;
      };

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${getQuerry()}&key=${
          process.env.NEXT_PUBLIC_GOOGLE_API_KEY
        }`
      );
      const data = await response.json();

      const updatePage = async () => {
        const longitude = data.results[0].geometry.location.lng;
        const latitude = data.results[0].geometry.location.lat;

        setViewport({
          ...viewport,
          longitude,
          latitude,
          zoom: 12,

          transitionDuration: 1400,
          transitionInterpolator: new FlyToInterpolator(),
        });
        setInitialViewport({ latitude, longitude });
      };

      if (data.results.length === 1 && filtered.length !== 1) {
        updatePage();
        setViewportTrigger(true);
      } else if (data.results.length && filtered.length === 1) {
        const { longitude, latitude } = filtered[0].position;
        setViewport({
          ...viewport,
          longitude,
          latitude,
          transitionDuration: 1400,
          zoom: 12,
          transitionInterpolator: new FlyToInterpolator(),
        });
        setInitialViewport({ latitude, longitude });

        setViewportTrigger(true);
      } else {
        const { longitude, latitude } = initialViewport;
        setViewport({
          ...viewport,
          latitude,
          longitude,
        });
        setInitialViewport({ latitude, longitude });
        setViewportTrigger(true);
      }

      if (isMobile && matches.current) {
        window.scrollTo(0, matches.current.offsetTop - 160);
      }
    }
  };

  const handleEnterKeyPress = e => {
    if (searchText.length && e.key === 'Enter') {
      handleSearchClick(e.currentTarget.value);
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const devCoordinates = { latitude: 52.3782, longitude: 4.853 };
      setUserCountryCode('NL');
      setViewport({
        ...viewport,
        ...devCoordinates,
      });
      setInitialViewport(devCoordinates);
      setSalons(salonData);
      setLoading(false);
    } else {
      getGeolocation();
    }
  }, []);

  useEffect(() => {
    const checkSalonNo = getSelectedSalonNo(locale);
    const checkSalonName = getStorageCustomerSalonName(locale);

    if (checkSalonNo && checkSalonName) {
      setSelectedSalon({ salonNo: checkSalonNo, salonName: checkSalonName });
    }
  }, []);

  const filterSalons = () => {
    const inRadius = handleSalonsInRadius(salons);
    if (activeFilters.includes(filtersList[0]?.toUpperCase())) {
      setSalonsInRadius(inRadius);
    } else {
      const filteredSalons = inRadius.filter(salon => {
        const isAmbasasdor = salon.salonLoyalty === true;
        const isKeune = salon.client100 === true;
        if (activeFilters.includes(filtersList[1]?.toUpperCase()) && isAmbasasdor) {
          return true;
        }
        if (activeFilters.includes(filtersList[2]?.toUpperCase()) && isKeune) {
          return true;
        }
        return false;
      });

      setSalonsInRadius(filteredSalons);
    }
  };

  useEffect(() => {
    if (viewportTrigger) {
      filterSalons();
    }
  }, [salons, viewport, viewportTrigger]);

  useEffect(() => {
    filterSalons();
  }, [activeFilters]);

  useEffect(() => {
    if (router.query.salonNo) {
      const favouriteSalon = salons.find(salon => salon.salonNo === router.query.salonNo);

      if (favouriteSalon) {
        setSalonFocus(favouriteSalon);
        setShowSalon(true);
      }
    }
  }, [salons]);

  useEffect(() => {
    if (customerEmail) {
      const fetchedSalons = salons.filter(
        s => s?.salonEmail === customerEmail || s?.parentEmail === customerEmail
      );
      if (fetchedSalons.length > 0) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useVisitorTypeTracking({
          visitorType: fetchedSalons[0].salonNo,
          location: { action: router },
        });
      }
      // eslint-disable-next-line react-hooks/rules-of-hooks
      else useVisitorTypeTracking({ visitorType: 'KEUNE Cosmetics', location: { action: router } });
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    else useVisitorTypeTracking({ visitorType: 'KEUNE Cosmetics', location: { action: router } });
  }, [router]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [showSalon]);

  return (
    <Layout locale={locale} globalModules={globalModules}>
      <>
        <Head>
          <title>{pageTitle?.text} | Keune Haircosmetics</title>
        </Head>
        <noscript>
          <div className={styles.noScriptMessage}>{javascriptDisabledMessage?.text}</div>
        </noscript>
        {loading && salons.length === 0 && (
          <div className={styles.loading}>
            <Icon type="loading" className={styles.loader} />
          </div>
        )}
        {!loading && salons.length && (
          <div className={styles.findSalons}>
            {showSalon && (
              <div className={styles.salonsContainer}>
                <SalonDetails
                  salon={salonFocus}
                  active={active}
                  locale={locale}
                  hoverPin={hoverPin}
                  closedText="Closed"
                  languageCode="en"
                  activeFilters={activeFilters}
                  selectedSalon={selectedSalon}
                  shopNowButton={shopNowButton}
                  shopNowContent={shopNowContent}
                  salonsInRadius={salonsInRadius}
                  initialViewport={initialViewport}
                  backButtonContent={backButtonContent}
                  getDirectionsPointer={getDirectionsPointer}
                  secondsalonsfinderpage={secondsalonsfinderpage}
                  thirdsalonsfinderpage={thirdsalonsfinderpage}
                  keuneAmbassadorSalonContent={keuneAmbassadorSalonContent}
                  setHovered={setHovered}
                  setShowSalon={setShowSalon}
                  setSearchText={setSearchText}
                  setSalonFocus={setSalonFocus}
                  setSelectedSalon={setSelectedSalon}
                  setActiveFilters={setActiveFilters}
                  setDisableSearch={setDisableSearch}
                  setSalonsInRadius={setSalonsInRadius}
                  setShowSuggestions={setShowSuggestions}
                  defaultLanguage={defaultLanguage}
                />
              </div>
            )}

            {!showSalon && (
              <div className={cn(styles.salonsContainer, !showSalon && styles.addWidth)}>
                <div className={styles.isSticky}>
                  {!isMobile && <h1 className={styles.title}>{pageTitle?.text}</h1>}
                  <SearchBar
                    viewport={viewport}
                    searchText={searchText}
                    activeFilters={activeFilters}
                    disableSearch={disableSearch}
                    initialViewport={initialViewport}
                    showSuggestions={showSuggestions}
                    userCountryCode={userCountryCode}
                    localeQuery={localeQuery}
                    selectedSuggestion={selectedSuggestion}
                    searchBarPlaceholder={searchBarPlaceholder}
                    handleSearchClick={handleSearchClick}
                    handleEnterKeyPress={handleEnterKeyPress}
                    setDisableSearch={setDisableSearch}
                    setViewport={setViewport}
                    setSearchText={setSearchText}
                    setActiveFilters={setActiveFilters}
                    setViewportTrigger={setViewportTrigger}
                    setShowSuggestions={setShowSuggestions}
                    setSelectedSuggestion={setSelectedSuggestion}
                  />
                  <button
                    type="button"
                    className={styles.useLocationButton}
                    disabled={gaveGeoPermission.state === 'denied'}
                    onClick={getGeolocation}
                    onKeyDown={getGeolocation}
                  >
                    {decideLocationButtonText()}
                  </button>
                  {filtersList && !isMobile && (
                    <div className={styles.filtersContainer}>
                      <div className={styles.filtersLabel}>
                        <span className={styles.filtersPointer}>{searchFiltersPointer?.text}</span>
                        {!isAllSalonsFilterDefault && (
                          <span className={styles.filtersDescription}>
                            {searchFilterDescription?.text}
                          </span>
                        )}
                      </div>
                      <div className={styles.filtersButtons}>
                        {filtersList.map(name => (
                          <Filter
                            key={name}
                            content={name.toUpperCase()}
                            filtersList={filtersList}
                            activeFilters={activeFilters}
                            setActiveFilters={setActiveFilters}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <>
                  {isMobile && (
                    <>
                      <Map
                        loading={loading}
                        hovered={hovered}
                        viewport={viewport}
                        allSalons={salons}
                        salonsInRadius={salonsInRadius}
                        initialViewport={initialViewport}
                        setInitialViewport={setInitialViewport}
                        setHovered={setHovered}
                        setViewport={setViewport}
                        handleSalonFocus={handleSalonFocus}
                        setViewportTrigger={setViewportTrigger}
                      />
                      {filtersList && (
                        <div className={styles.filtersContainer}>
                          <div className={styles.filtersLabel}>
                            <span className={styles.filtersPointer}>
                              {searchFiltersPointer?.text}
                            </span>
                            {!isAllSalonsFilterDefault && (
                              <span className={styles.filtersDescription}>
                                {searchFilterDescription?.text}
                              </span>
                            )}
                          </div>
                          <div className={styles.filtersButtons}>
                            {filtersList.map(name => (
                              <Filter
                                key={name}
                                content={name.toUpperCase()}
                                filtersList={filtersList}
                                activeFilters={activeFilters}
                                setActiveFilters={setActiveFilters}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
                {salonsInRadius && (
                  <div ref={matches}>
                    <MatchesList
                      hovered={hovered}
                      salons={salons}
                      salonsInRadius={salonsInRadius}
                      initialViewport={initialViewport}
                      resultsLimit={resultsLimit}
                      setActive={setActive}
                      setHovered={setHovered}
                      setShowSalon={setShowSalon}
                      setSalonFocus={setSalonFocus}
                    />
                  </div>
                )}
              </div>
            )}
            {!showSalon && !isMobile && salonsInRadius && (
              <Map
                loading={loading}
                hovered={hovered}
                viewport={viewport}
                allSalons={salons}
                salonsInRadius={salonsInRadius}
                initialViewport={initialViewport}
                setInitialViewport={setInitialViewport}
                setHovered={setHovered}
                setViewport={setViewport}
                handleSalonFocus={handleSalonFocus}
                setViewportTrigger={setViewportTrigger}
              />
            )}
          </div>
        )}
      </>
    </Layout>
  );
}

export async function getStaticProps({ locale }) {
  const salonsResponse = await db.collection('salons').get();
  const fullSalonData = salonsResponse.docs.map(doc => doc.data());
  const salons = fullSalonData.map(salon => {
    // eslint-disable-next-line no-param-reassign
    delete salon.orders;
    return {
      ...salon,
    };
  });

  const globalModules = await getGlobal(
    [
      'announcement-bar',
      'cookies',
      'salonsfinderpage',
      'secondsalonsfinderpage',
      'firstcountryselectorlocales',
      'thirdsalonsfinderpage',
      'productpage',
    ],
    reformatLocaleToContentfulLocale(locale)
  );

  const localeHasSalonFinder = globalModules.settings.localesWithSalonFinder.onOrOff;
  if (!localeHasSalonFinder) {
    return {
      notFound: true,
    };
  }

  // add resources
  return {
    props: { salonData: salons, globalModules, locale },
  };
}
